import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BRAND } from "../lib/data";

import fr from "../i18n/fr";
import en from "../i18n/en";

export default function Footer() {
  const location = useLocation();

  const isEnglish = location.pathname.startsWith("/en");
  const t = isEnglish ? en : fr;

  const contactPath = isEnglish ? "/en/contact" : "/fr/contact";
  const legalPath = isEnglish
    ? "/en/legal-notice"
    : "/fr/mentions-legales";
  const staffPath = isEnglish ? "/en/admin" : "/fr/admin";

  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/10 bg-void"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">

          {/* =====================================================
              CTA
          ====================================================== */}

          <div className="md:col-span-6">
            <Link
              to={contactPath}
              data-testid="footer-cta"
              className="group inline-block"
            >
              <span className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
                {t.footer.ctaLine1}
                <br />

                <span className="text-acid group-hover:text-white transition-colors duration-300">
                  {t.footer.ctaLine2}
                </span>

                <ArrowUpRight
                  className="inline-block ml-2 mb-2"
                  size={44}
                />
              </span>
            </Link>

            <p className="mt-8 font-sans font-light text-zinc-400 max-w-md">
              {t.footer.description}
            </p>
          </div>

          {/* =====================================================
              CONTACT
          ====================================================== */}

          <div className="md:col-span-3">
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5">
              {t.footer.contact}
            </p>

            <ul className="space-y-3 font-sans text-zinc-300">
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="hover:text-acid transition-colors"
                  data-testid="footer-email"
                >
                  {BRAND.email}
                </a>
              </li>

              <li>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="hover:text-acid transition-colors"
                  data-testid="footer-phone"
                >
                  {BRAND.phone}
                </a>
              </li>
            </ul>
          </div>

          {/* =====================================================
              SOCIALS
          ====================================================== */}

          <div className="md:col-span-3">
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5">
              {t.footer.socials}
            </p>

            <ul className="space-y-3 font-sans text-zinc-300">
              <li>
                <a
                  href={BRAND.socials.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-acid transition-colors inline-flex items-center gap-1"
                  data-testid="footer-instagram"
                >
                  Instagram
                  <ArrowUpRight size={14} />
                </a>
              </li>

              <li>
                <a
                  href={BRAND.socials.tiktok.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-acid transition-colors inline-flex items-center gap-1"
                  data-testid="footer-tiktok"
                >
                  TikTok
                  <ArrowUpRight size={14} />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* =====================================================
            LEGAL / BOTTOM
        ====================================================== */}

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 font-mono-tech text-xs text-zinc-600 uppercase tracking-[0.15em]">

          <span>
            © {year} {BRAND.name} — {t.footer.microBusiness}
          </span>

          <span>
            SIRET {BRAND.siret}
          </span>

          <Link
            to={legalPath}
            data-testid="footer-legal"
            className="hover:text-acid transition-colors"
          >
            {t.footer.legal}
          </Link>

          <Link
            to={staffPath}
            data-testid="footer-admin"
            className="hover:text-acid transition-colors"
          >
            {t.footer.staffArea}
          </Link>
        </div>
      </div>
    </footer>
  );
}